import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import JsonLd from "../../components/JsonLd";

const SAVI = "https://www.savicoliving.com";

// Fact-checked Oct 2026. Keep this honest: every entry says who it's actually best for.
const LIST = [
  {
    name: "Savi Coliving",
    where: "Sierra Calderona, 25 minutes from Valencia",
    url: SAVI,
    internal: true,
    model: "A 16-room villa in the mountains. Alcohol and drug free for guests and staff. Pool, gym, yoga studio, coworking, shared dinners, three resident dogs. Stays of a month or more, application only.",
    bestFor: "alcohol-free living, deep work and long stays. Winner of Best Destination Coliving at the Coliving Awards 2026.",
    notFor: "a party crowd, a two-night stay or anyone who wants a city outside the door.",
  },
  {
    name: "Nine Coliving",
    where: "La Orotava, Tenerife",
    url: "https://ninecoliving.com",
    model: "A historic Canarian townhouse for up to 15 guests, with yoga, community breakfasts and a 15-day minimum stay.",
    bestFor: "a small, close group and a proper island winter.",
    notFor: "privacy or a quiet evening in; it runs on togetherness.",
  },
  {
    name: "Sende",
    where: "Senderiz, Ourense, Galicia",
    url: "https://sende.co",
    model: "One of Europe's earliest colivings, spread over village houses in a tiny Galician hamlet. Monthly stays, gardens, workspaces, very little else for miles.",
    bestFor: "rural quiet, slow months and people who like weather.",
    notFor: "anyone who needs a supermarket within walking distance.",
  },
  {
    name: "Anceu Coliving",
    where: "Anceu, Pontevedra, Galicia",
    url: "https://anceu.com",
    model: "A restored stone house with a stated mission to help repopulate rural Galicia. Fifteen-day minimum, solid remote-work setup, creative and nature-led crowd.",
    bestFor: "purpose-driven stays and makers who want to be useful to a village.",
    notFor: "sun seekers. It rains, and they're honest about it.",
  },
  {
    name: "Rooral",
    where: "Benarrabá, Serranía de Ronda, Málaga",
    url: "https://rooral.co",
    model: "Seasonal cohorts in a white mountain village. Two-week minimum, Sunday check-ins, and a programme built around meeting the neighbours rather than each other.",
    bestFor: "village life and a cohort with a start and end date.",
    notFor: "flexible dates or a year-round base; it runs in seasons.",
  },
  {
    name: "Nomadico",
    where: "Tarifa (October to March) and rural Mallorca",
    url: "https://nomadico.io",
    model: "Seasonal houses with 19 rooms across two connected Tarifa properties and a finca in Mallorca. Desks in every room, surf on the doorstep.",
    bestFor: "surf, wind and a sociable winter season.",
    notFor: "a summer in Tarifa; the house closes.",
  },
  {
    name: "Repeople",
    where: "Las Palmas de Gran Canaria and Tenerife",
    url: "https://repeople.co",
    model: "A remote-work ecosystem rather than a single house: coliving, coworking and corporate housing across several properties.",
    bestFor: "city-based nomads who want a ready-made network and a coworking desk.",
    notFor: "a single-house community feel; it's a network, not a villa.",
  },
  {
    name: "Cactus Coliving",
    where: "Valle San Lorenzo and Adeje, Tenerife, plus La Gomera",
    url: "https://cactuscoliving.com",
    model: "Three Canarian houses in historic buildings, one on the quieter island of La Gomera. Hiking-heavy, beach-adjacent, long-running.",
    bestFor: "outdoor people who want options across two islands.",
    notFor: "anyone who wants the whole house to be on the same schedule.",
  },
  {
    name: "Urban Campus",
    where: "Madrid, Valencia and Málaga",
    url: "https://urbancampus.com",
    model: "The professional end of the market: private studios, gyms, rooftops and coworking in city buildings. Three-month minimum in Valencia, from about €920 a month.",
    bestFor: "city life, a lease-like setup and your own front door.",
    notFor: "community as the main event; it's residential first.",
  },
  {
    name: "Enso Coliving",
    where: "Barcelona, Madrid and Valencia",
    url: "https://ensocoliving.com",
    model: "Furnished rooms in shared flats across Spain's three big cities, with an international, longer-stay crowd.",
    bestFor: "a first base in a Spanish city with flatmates already in place.",
    notFor: "nature, pools or a shared kitchen table every night.",
  },
];

const itemList = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Best Coliving Spaces in Spain (2026)",
  description: "An honest, fact-checked list of ten coliving operators in Spain, each with who it's actually best for.",
  itemListOrder: "https://schema.org/ItemListOrderAscending",
  numberOfItems: LIST.length,
  itemListElement: LIST.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    url: c.url,
    item: { "@type": "LodgingBusiness", name: c.name, url: c.url, description: c.model, address: { "@type": "PostalAddress", addressCountry: "ES", addressLocality: c.where } },
  })),
};

const Entry = ({ n, c }) => (
  <section className={c.internal ? "bg-[#F4ECE2] rounded-2xl p-5 sm:p-6 -mx-2 sm:-mx-4" : ""} data-testid={`best-spain-${n}`}>
    <h2 className={c.internal ? "!mt-0" : ""}>
      {n}. {c.name} <span className="text-[#2E2A26]/50 font-sans text-base">· {c.where}</span>
    </h2>
    <p>{c.model}</p>
    <p><strong>Best for:</strong> {c.bestFor}</p>
    <p><strong>Not for:</strong> {c.notFor}</p>
    <p>
      {c.internal ? (
        <Link to="/rooms" className="text-[#C85A38] hover:underline">See rooms and dates →</Link>
      ) : (
        <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-[#C85A38] hover:underline">{c.url.replace("https://", "")} →</a>
      )}
    </p>
  </section>
);

const BestColivingSpain = () => (
  <BlogLayout
    title="Best Coliving Spaces in Spain (2026)"
    metaTitle="Best Coliving Spaces in Spain 2026 | An Honest List of 10"
    metaDesc="Ten coliving spaces in Spain that are actually open in 2026, each with who it's best for and who it isn't. Written by a coliving operator, fact-checked, no affiliate links."
    canonical="/best-coliving-spain"
    datePublished="2026-10-18"
    dateModified="2026-10-18"
  >
    <JsonLd id="best-coliving-spain-list" data={itemList} />

    <p>Most 'best coliving in Spain' lists are written by people who have never run one, and at least one of them still has a house at number one that closed in 2025. We run <Link to="/" className="text-[#C85A38] hover:underline">Savi Coliving</Link>, so we're biased, and we've put ourselves at the top. The rest of the list is honest. Every entry says who the place is actually best for, and who should look elsewhere.</p>

    <p>Spain has roughly three kinds of coliving now: <strong>rural houses</strong> built around a small community (Savi, Sende, Anceu, Rooral), <strong>seasonal nomad houses</strong> by the sea (Nine, Nomadico, Cactus) and <strong>city operators</strong> that are closer to flexible renting (Urban Campus, Enso, Repeople). Pick the kind first. Then the house.</p>

    {LIST.map((c, i) => <Entry key={c.name} n={i + 1} c={c} />)}

    <h2>Closed, but still on everyone's list</h2>
    <p><strong>Sun and Co, Jávea.</strong> Spain's best-known coliving opened in October 2015 and closed after its final season on 21 June 2025. It still appears at the top of lists published in 2026, which tells you how those lists get made. We mention it because people keep searching for it; if you loved what it stood for, the rural houses above are the closest living relatives.</p>

    <h2>How we chose</h2>
    <p>Every place here was checked in October 2026 for a working website, current dates or availability and a real address. We left out operators we couldn't verify, anything that is really a hostel, and the big Barcelona room-rental brands that call themselves coliving but don't share a table. No affiliate links, no paid placements. If a fact is wrong, tell us at <a href="mailto:hola@savicoliving.com" className="text-[#C85A38] hover:underline">hola@savicoliving.com</a> and we'll fix it.</p>

    <h2>Who shouldn't come to Savi</h2>
    <p>Plainly: if you want a drink after work, a city outside the door or a stay under a month, two or three of the houses above will suit you better and we'd rather you were happy there. Savi is for people who want a clear head, a long stay and a house that runs properly. That's a smaller group. It's the one we built for.</p>

    <p>Related: <Link to="/best-sober-coliving-europe" className="text-[#C85A38] hover:underline">best sober coliving in Europe</Link> · <Link to="/coliving-valencia" className="text-[#C85A38] hover:underline">coliving in Valencia</Link> · <Link to="/coliving-vs-retreat-vs-sober-coliving" className="text-[#C85A38] hover:underline">coliving vs retreat vs sober coliving</Link></p>
  </BlogLayout>
);

export default BestColivingSpain;
